/** @format */

import { type FC, memo, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Typography } from "senf-atomic-design-system";
import { useAuthContext } from "senf-shared";
import Box from "../../atoms/box/Box";
import Button from "../../core/button/Button";
import { useModals } from "../modalStack/ModalProvider";

const Wrapper = styled.div<{ hide: boolean }>`
  box-sizing: border-box;
  position: fixed;
  top: 60px;
  margin: 16px;
  height: auto;
  z-index: ${({ bringUpZindex }) => (bringUpZindex ? 9999999999999999999 : 1)};
  width: calc(100% - 32px);
  background: linear-gradient(
      0deg,
      rgba(134, 124, 99, 0.01),
      rgba(134, 124, 99, 0.01)
    ),
    #ffffff;
  border: 2px solid #ffffff;
  box-shadow:
    0px 20px 60px rgba(134, 124, 99, 0.15),
    0px -5px 10px rgba(255, 255, 255, 0.3),
    0px 10px 20px -6px rgba(134, 124, 99, 0.1);
  border-radius: 14px;
  transform: ${({ hide }) => (hide ? "scale(0.9)" : "scale(1)")};
  opacity: ${({ hide }) => (hide ? "0" : "1")};
  pointer-events: ${({ hide }) => (hide ? "none" : "all")};

  transition: 0.3s;

  @media (min-width: 768px) {
    width: 300px;
    right: 0;
  }
`;

const ModalContent = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();

  return (
    <Box padding="0 20px 20px 20px" flexDirection="column">
      <Typography variant="bodyBg">
        <b>{user?.handle || t("global:guestUser")}</b>, du wirst automatisch
        ausgeloggt in... <b style={{ color: "hsla(1,89%,59%,1)" }} id="timer" />
      </Typography>
    </Box>
  );
};

const WorkshopTopbar: FC = ({}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { projectId, surveyId, ideaMapId } = useParams();
  const navigate = useNavigate();
  const { user, signOut } = useAuthContext();
  const { openModal, closeModal } = useModals();
  const [hide, setHide] = useState(false);

  const handleSignOut = () => {
    signOut();
    setTimeout(() => {
      if (surveyId) {
        navigate(`/projects/${projectId}/survey/${surveyId}`);
        window?.location?.reload();
      } else if (ideaMapId) {
        navigate(`/projects/${projectId}/ideaMaps/${ideaMapId}`);
        window?.location?.reload();
      } else {
        navigate(`/projects/${projectId}/info`);
      }
    }, 50);
  };

  const timer = useRef(null);

  function showTimerDiv(seconds) {
    const timerDiv = document.getElementById("timer");
    timerDiv.style.display = "block";
    if (timer.current) {
      clearInterval(timer.current);
    }

    timerDiv.innerHTML = `${seconds} Sekunden`;

    timer.current = setInterval(() => {
      seconds--;
      timerDiv.innerHTML = `${seconds} Sekunden`;

      if (seconds <= 0) {
        handleSignOut();
        clearInterval(timer.current);
        timerDiv.style.display = "none";
        // if (typeof callback === "function") {
        //   callback();
        // }
      }
    }, 1000);
  }

  function checkIfInactive() {
    let modalOpener = setTimeout(() => {
      setHide(true);
      openModal(<ModalContent />, {
        title: `Bist du noch aktiv?`,
        cancelText: "Eingeloggt bleiben",
        submitText: t("auth:logout"),
        critical: true,
        onSubmit: handleSignOut,
        onClose: () => {
          setHide(false);
        },
      });
      setTimeout(() => {
        showTimerDiv(30);
      }, 100);
    }, 30000);

    document.addEventListener("mousemove", resetTimer);
    document.addEventListener("mousedown", resetTimer);
    document.addEventListener("keydown", resetTimer);

    function resetTimer() {
      clearTimeout(modalOpener);
      clearInterval(timer.current);
      modalOpener = setTimeout(() => {
        setHide(true);
        openModal(<ModalContent />, {
          title: `Bist du noch aktiv?`,
          cancelText: "Eingeloggt bleiben",
          submitText: t("auth:logout"),
          critical: true,
          onSubmit: handleSignOut,

          onClose: () => {
            setHide(false);
          },
        });
        setTimeout(() => {
          showTimerDiv(30);
        }, 100);
      }, 30000);
    }
  }

  useEffect(() => {
    if (pathname.includes("info")) {
      setHide(true);
    } else {
      setHide(false);
    }

    if (!pathname.includes("survey")) {
      checkIfInactive();
    }
  }, [pathname]);

  return (
    <Wrapper hide={hide} bringUpZindex={pathname.includes("survey")}>
      <Box
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        gap="10px"
        padding="20px"
      >
        <Typography variant="buttonBg" textAlign="center">
          {t("global:youAreStillSignedInAs")} "
          {user?.handle || t("global:guestUser")}"
        </Typography>

        <Button
          text={t("auth:logout")}
          width="max"
          variant="primary"
          critical
          onClick={handleSignOut}
        />
      </Box>
    </Wrapper>
  );
};

export default memo(WorkshopTopbar);
